import { useCallback, useEffect, useState } from "react";
import { markIntentionalFlowPause } from "@/utils/sentry/intentional";
import useTabsValidation from "@/hooks/useTabsValidation";
import useInterval from "@/hooks/useInterval/useInterval";
import { requestScheduleActivation } from "@/api/sale/scheduleActivation";
import { requestSMSConfirmation, requestSMSSending } from "@/api/sms";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import Layout from "@/components/Layout";
import Column from "@/components/Column";
import Form from "@/components/Form";
import Columns from "@/components/Columns";
import Button from "@/components/Button";
import VerificationCode from "@/components/VerificationCode";
import ErrorText from "@/components/ErrorText";
import { sendStepUserJourney } from "@/api/userJourney";
import Text from "@/components/Text";
import usePageSubmit from "@/hooks/usePageSubmit";
import SEO from "@/components/SEO";
import { requestAnalysis } from "@/api/eligibility/analysis";
import { useModalStore } from "@/store/modal";
import { useTagManager } from "@/hooks/useTagManager";
import { getOnlyNumbers } from "@/utils";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { formatPrice } from "@/utils/currency";

const pageConfig = {
  route: "/payment-data/sms",
  prevRoute: "/payment-data",
  nextRoute: "/",
};

// Confirm SMS Fields
export const ConfirmSMSPage = () => {
  useTabsValidation(pageConfig.route, pageConfig.prevRoute);
  const [error, setError] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [, setFormIsValid] = useState(false);
  const [clearVerificationCode, setClearVerificationCode] = useState(false);
  const [nextRoute, setNextRoute] = useState("");
  const [timer, setTimer] = useInterval();
  const phoneNumber = useFormStore((store) => store.value.tel);
  const saleId = useFormStore((store) => store.saleId);
  const setShowModalCustumerValidation = useModalStore(
    (store) => store.setShowModalCustomerValidation
  );
  const setUrlValidation = useModalStore((store) => store.setUrlValidation);
  const [sendSMSIsLoading, setSendSMSIsLoading] = useState(false);
  const [analysisStatusLoading, setAnalysisStatusLoading] = useState(false);

  const handleTokenResend = async () => {
    setSmsCode("");
    setClearVerificationCode(true);
    try {
      setSendSMSIsLoading(true);
      await requestSMSSending({ saleID: saleId!, msisdn: phoneNumber }).finally(
        () => setSendSMSIsLoading(false)
      );
      setClearVerificationCode(false);
      setTimer(60);
    } catch (err) {
      setError(
        `Aconteceu um erro ao reenviar seu token. Por favor, tente novamente.`
      );
      // Erro real ja foi capturado pelo interceptor do axios; throw aqui eh
      // apenas para "pausar" o fluxo apos exibir mensagem ao usuario.
      throw markIntentionalFlowPause(err);
    }
  };
  const uuid = useFormStore((store) => store.value.uuid);

  const performAnalysisHandlingWithReturn = useCallback(
    async (saleID: number) => {
      setAnalysisStatusLoading(true);
      await requestAnalysis({
        venda_id: saleID,
      })
        .then((response) => {
          if (response?.url) {
            setShowModalCustumerValidation(true);
            setUrlValidation(response?.url);
          }
        })
        .finally(() => setAnalysisStatusLoading(false));
    },
    [setShowModalCustumerValidation, setUrlValidation]
  );

  useEffect(() => {
    setFormIsValid(smsCode.length === 4);
  }, [smsCode]);

  const [SMSConfirmationLoading, setSMSConfirmationLoading] = useState(false);
  const { dataLayer } = useTagManager();
  const productStore = useProductStore((store) => store.item);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const tel = useFormStore((store) => store.value.tel);
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(tel).slice(0, 2))
  );
  const handleSMSReceiver = useCallback(
    async ({ sms }: { sms?: string } = {}) => {
      try {
        setSMSConfirmationLoading(true);
        await requestSMSConfirmation({
          saleID: saleId!,
          msisdn: phoneNumber,
          sms: sms || smsCode,
        })
          .then(() => {
            dataLayer({
              event: "step",
              passo: "SMS",
              sku: removeAccentsAndSetUpperCase(productStore?.tim_id),
              nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
                productStore?.nome
              )}-${totalGbProduct}`,
              valor: formatPrice(currentPrice),
              status: "Sucesso",
            });
          })
          .finally(() => setSMSConfirmationLoading(false));
        await requestScheduleActivation(saleId!);
        await performAnalysisHandlingWithReturn(saleId!);
      } catch (err) {
        const apiError =
          (
            err as {
              response?: {
                data?: { errors?: { message?: string }[]; message?: string };
              };
            }
          )?.response?.data?.errors?.[0]?.message ||
          (
            err as {
              response?: {
                data?: { errors?: { message?: string }[]; message?: string };
              };
            }
          )?.response?.data?.message;

        setError(
          apiError ||
            "Por favor, digite o código de segurança válido e continue"
        );
        dataLayer({
          event: "step",
          passo: "SMS",
          sku: removeAccentsAndSetUpperCase(productStore?.tim_id),
          nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
            productStore?.nome
          )}-${totalGbProduct}`,
          valor: formatPrice(currentPrice),
          status: "Erro",
        });
        // Erro real ja foi capturado pelo interceptor do axios; throw aqui eh
        // apenas para "pausar" o fluxo apos exibir mensagem ao usuario.
        throw markIntentionalFlowPause(err);
      }
    },
    [
      saleId,
      phoneNumber,
      smsCode,
      performAnalysisHandlingWithReturn,
      dataLayer,
      productStore?.tim_id,
      productStore?.nome,
      totalGbProduct,
      currentPrice,
    ]
  );

  const productType = useProductStore((store) => store.payment?.type);

  useEffect(() => {
    setNextRoute("/confirmation/auto-debt");
  }, [productType]);

  useEffect(() => {
    sendStepUserJourney({
      passo: "TOKEN",
      uuid,
    });
  }, [uuid]);

  const handleOnSubmit = usePageSubmit(nextRoute, handleSMSReceiver);

  const handleOnComplete = (sms: string) => {
    setSmsCode(sms);
    handleOnSubmit({ sms });
  };
  const handleOnChange = () => setError("");

  return (
    <Layout>
      <SEO
        title="Validação de SMS"
        description="Confirme o seu número com o token enviado"
      />

      <Columns as={Form} onSubmit={handleOnSubmit} justifyContent="center">
        <Column
          size={12}
          className="pb-8 text-center lg:pl-6"
          data-ic-title="sec-omega-comunicacao"
        >
          <Text kind="blue" size="base" className="text-left">
            Show!
          </Text>
          <Text kind="blue" size="base" className="text-left lg:mt-8">
            Para sua segurança e para confirmar seu pedido, preencha o código de
            segurança que enviamos por SMS e WhatsApp para o número {phoneNumber}
          </Text>
        </Column>

        <Column size={[12, 10]} className="pb-4 text-center">
          <VerificationCode
            fieldsCount={4}
            onChange={(e) => {
              setSmsCode(e);
              handleOnChange();
            }}
            onComplete={handleOnComplete}
            clear={clearVerificationCode}
            disabled={SMSConfirmationLoading}
          />
          {error && (
            <ErrorText showIcon={false} id="verification-error">
              {error}
            </ErrorText>
          )}
        </Column>

        {timer === 0 ? (
          <Column size={12} className="pb-1 text-center">
            <Button
              kind="secondary"
              onClick={handleTokenResend}
              loading={sendSMSIsLoading || analysisStatusLoading}
            >
              <span className="flex leading-normal">Reenviar Código</span>
            </Button>
          </Column>
        ) : (
          <Column
            size={12}
            className="pb-10 text-center font-normal text-blue-400"
          >
            Outro token poderá ser gerado em {timer} segundos
          </Column>
        )}

        <Column size={12} />

        <Column size={12}>
          <Button
            type="button"
            kind="secondary"
            large
            to={pageConfig.prevRoute}
            data-ic-trigger="sec-omega-voltar"
          >
            Voltar
          </Button>
        </Column>
      </Columns>
    </Layout>
  );
};

export default ConfirmSMSPage;
